import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ordersAPI } from '../services/api';
import { 
  Package, 
  Truck, 
  CheckCircle2, 
  ClipboardList, 
  XCircle, 
  Share2, 
  ArrowLeft,
  MapPin,
  Calendar
} from 'lucide-react';

const PublicOrderStatus = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const numericId = String(id).split('-')[0];
        const res = await ordersAPI.track(numericId);
        setOrder(res.data);
      } catch (e) {
        setError('Order not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const share = async () => {
    const url = `${window.location.origin}/share/orders/${order.id}`;
    const title = `Track Order #${order.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert('Tracking link copied to clipboard');
      }
    } catch {}
  };

  const statusSteps = [
    { key: 'pending', title: 'Order Placed', icon: ClipboardList },
    { key: 'confirmed', title: 'Confirmed', icon: CheckCircle2 },
    { key: 'shipped', title: 'Shipped', icon: Truck },
    { key: 'delivered', title: 'Delivered', icon: Package }
  ];

  const currentStatusIndex = statusSteps.findIndex(step => step.key === order?.status);
  const isCancelled = order?.status === 'cancelled';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <XCircle className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
        <p className="text-gray-600 mb-8 max-w-md">
          We couldn't find the order you're looking for. Please check the tracking link and try again.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-semibold transition-all hover:shadow-lg"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Track Order #{order.id}</h1>
            <p className="text-gray-500 mt-1">Real-time status of your purchase</p>
          </div>
          <button
            onClick={share}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-white border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
          >
            <Share2 className="w-4 h-4" />
            Share Tracking
          </button>
        </div>

        {/* Main Tracking Card */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 mb-8">
          <div className="p-6 sm:p-10">
            {/* Status Timeline */}
            <div className="relative mb-12">
              <div className="absolute top-5 left-0 w-full h-0.5 bg-gray-100 hidden sm:block"></div>
              <div className="relative flex flex-col sm:flex-row justify-between gap-8 sm:gap-0">
                {statusSteps.map((step, index) => {
                  const isCompleted = index <= currentStatusIndex && !isCancelled;
                  const isCurrent = index === currentStatusIndex && !isCancelled;
                  return (
                    <div key={step.key} className="flex sm:flex-col items-center gap-4 sm:gap-3 flex-1">
                      <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-all duration-500 ${
                        isCompleted ? 'bg-green-600 text-white scale-110' : 'bg-white text-gray-400 border-2 border-gray-100'
                      }`}>
                        <step.icon className="w-5 h-5" />
                        {isCurrent && (
                          <div className="absolute -inset-1 rounded-full border-2 border-green-600 animate-ping opacity-20"></div>
                        )}
                      </div>
                      <div className="text-left sm:text-center">
                        <p className={`text-sm font-bold ${isCompleted ? 'text-gray-900' : 'text-gray-400'}`}>
                          {step.title}
                        </p>
                        {isCurrent && (
                          <p className="text-xs text-green-600 font-medium">In Progress</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {isCancelled && (
              <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-600" />
                <p className="text-sm font-medium text-red-800">This order has been cancelled.</p>
              </div>
            )}

            {/* Order Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-gray-50">
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900">Delivery Details</h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Shipping Address</p>
                      <p className="text-sm text-gray-700 leading-relaxed mt-1">{order.user?.address || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Order Date</p>
                      <p className="text-sm text-gray-700 mt-1">
                        {new Date(order.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'long', day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Items Subtotal</span>
                    <span>₹{(Number(order.items_total) || (Number(order.total_amount || 0) - Number(order.delivery_charge !== undefined ? order.delivery_charge : 50))).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Delivery Charge</span>
                    <span className="text-green-600">+ ₹{Number(order.delivery_charge !== undefined ? order.delivery_charge : 50).toFixed(2)}</span>
                  </div>
                  <div className="pt-3 border-t border-gray-200 flex justify-between">
                    <span className="font-bold text-gray-900">Total Charged</span>
                    <span className="font-bold text-green-600 text-lg">
                      {order.formatted_total_amount || `₹${parseFloat(order.total_amount || 0).toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <h2 className="font-bold text-gray-900">Order Items</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {order.order_items.map(item => (
              <div key={item.id} className="p-6 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                <div className="w-16 h-16 rounded-xl overflow-hidden border border-gray-100 shrink-0 bg-white">
                  <img 
                    src={item.product.image_url} 
                    alt={item.product.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/100x100?text=No+Image'; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 truncate">{item.product.name}</p>
                  <p className="text-sm text-gray-500 mt-0.5">Quantity: {item.quantity}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{item.formatted_total_price}</p>
                  <p className="text-xs text-gray-500">{item.product.formatted_price} each</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/"
            className="text-green-600 font-bold hover:text-green-700 transition"
          >
            ← Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicOrderStatus;


